// BackgroundUploader.jsx - PowerPoint-style background image options
import React, { useRef } from 'react';
import { Image, Upload, X } from 'lucide-react';
import { compressImage } from '../../../utils/imageCompressor';

const FIT_OPTIONS = [
  { value: 'contain', label: 'Fit',     desc: 'Fit within canvas, maintain ratio' },
  { value: 'cover',   label: 'Fill',    desc: 'Fill canvas, crop edges, maintain ratio' },
  { value: 'stretch', label: 'Stretch', desc: 'Stretch to fill, may distort' },
  { value: 'tile',    label: 'Tile',    desc: 'Repeat as pattern' },
];

// 3×3 alignment grid: [svgValue, label]
const ALIGN_GRID = [
  ['xMinYMin', 'xMidYMin', 'xMaxYMin'],
  ['xMinYMid', 'xMidYMid', 'xMaxYMid'],
  ['xMinYMax', 'xMidYMax', 'xMaxYMax'],
];

export default function BackgroundUploader({
  backgroundImage,
  backgroundOpacity = 0.3,
  backgroundFit = 'contain',
  backgroundAlignment = 'xMidYMid',
  backgroundTileSize = 200,
  onImageChange,
  onOpacityChange,
  onFitChange,
  onAlignmentChange,
  onTileSizeChange,
  onClear,
  darkMode = false
}) {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      alert('Please upload PNG, JPG, SVG, or WebP images.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const compressed = await compressImage(event.target.result, { maxDimension: 1200 });
      onImageChange(compressed);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const input = fileInputRef.current;
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      handleFileChange({ target: input });
    }
  };

  const accent = '#6366f1';
  const text = darkMode ? '#e5e7eb' : '#111827';
  const textMuted = darkMode ? '#9ca3af' : '#6b7280';
  const border = darkMode ? '#374151' : '#e5e7eb';
  const card = darkMode ? '#1a1d29' : '#f9fafb';
  const bg = darkMode ? '#0f1117' : '#ffffff';

  const showAlignment = backgroundFit === 'contain' || backgroundFit === 'cover';
  const showTileSize = backgroundFit === 'tile';

  return (
    <div style={{ padding: '12px', background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: '8px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Image size={16} color={accent} />
        <span style={{ fontSize: '13px', fontWeight: '600', color: text }}>Canvas Background</span>
      </div>

      {!backgroundImage ? (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${border}`, borderRadius: '8px', padding: '20px',
            textAlign: 'center', cursor: 'pointer', background: bg
          }}
        >
          <Upload size={24} color={textMuted} style={{ marginBottom: '8px' }} />
          <p style={{ margin: 0, fontSize: '12px', color: textMuted }}>Drop image or click to upload</p>
          <p style={{ margin: '4px 0 0', fontSize: '10px', color: textMuted }}>PNG, JPG, SVG, WebP (max 5MB)</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Preview */}
          <div style={{ position: 'relative', borderRadius: '6px', overflow: 'hidden', background: card, height: '70px' }}>
            <img src={backgroundImage} alt="Background" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: backgroundOpacity }} />
            <button
              onClick={onClear}
              style={{
                position: 'absolute', top: '4px', right: '4px',
                width: '22px', height: '22px', borderRadius: '4px', border: 'none',
                background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <X size={12} />
            </button>
          </div>

          {/* Fit mode buttons */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: textMuted, marginBottom: '6px' }}>Fit Mode</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
              {FIT_OPTIONS.map(opt => {
                const active = backgroundFit === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => onFitChange(opt.value)}
                    title={opt.desc}
                    style={{
                      padding: '6px 4px', border: `1px solid ${active ? accent : border}`,
                      borderRadius: '5px', cursor: 'pointer',
                      background: active ? accent + '22' : 'transparent',
                      color: active ? accent : text,
                      fontSize: '11px', fontWeight: active ? '700' : '500'
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Alignment grid (for Fit and Fill) */}
          {showAlignment && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: textMuted, marginBottom: '6px' }}>Position</div>
              <div style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 26px)', gap: '3px' }}>
                {ALIGN_GRID.map((row, ri) =>
                  row.map((val, ci) => {
                    const active = backgroundAlignment === val;
                    return (
                      <button
                        key={val}
                        onClick={() => onAlignmentChange(val)}
                        title={val}
                        style={{
                          width: '26px', height: '26px', border: `1px solid ${active ? accent : border}`,
                          borderRadius: '4px', cursor: 'pointer',
                          background: active ? accent : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: 0
                        }}
                      >
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: active ? '#fff' : textMuted
                        }} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Tile size (for Tile mode) */}
          {showTileSize && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: '600', color: textMuted }}>Tile Size</span>
                <span style={{ fontSize: '11px', color: textMuted }}>{backgroundTileSize}px</span>
              </div>
              <input
                type="range" min="40" max="600" step="10"
                value={backgroundTileSize}
                onChange={(e) => onTileSizeChange(Number(e.target.value))}
                style={{ width: '100%', cursor: 'pointer', accentColor: accent }}
              />
            </div>
          )}

          {/* Opacity */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: textMuted }}>Opacity</span>
              <span style={{ fontSize: '11px', color: textMuted }}>{Math.round(backgroundOpacity * 100)}%</span>
            </div>
            <input
              type="range" min="0.05" max="1" step="0.05"
              value={backgroundOpacity}
              onChange={(e) => onOpacityChange(parseFloat(e.target.value))}
              style={{ width: '100%', cursor: 'pointer', accentColor: accent }}
            />
          </div>

          {/* Change image */}
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: '100%', padding: '6px', border: `1px solid ${border}`,
              borderRadius: '4px', background: 'transparent', color: text,
              fontSize: '11px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
            }}
          >
            <Upload size={12} />
            Change Image
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
}
